import {
  DEFAULT_SORT_ORDER,
  NEWEST,
  OLDEST,
  NEWEST_BY_COLLABORATOR,
  OLDEST_BY_COLLABORATOR,
  LONGEST_UNANSWERED,
  SHORTEST_UNANSWERED,
} from 'constants/defaults'
import { uniq } from 'util/arrays'

function extractSearchTicketIds(search) {
  if (!search) return null
  const { pages } = search
  if (!pages) return null
  const pageValues = Object.values(pages)
  if (pageValues.length === 0) return []
  const ids = pageValues.reduce((a, b) => a.concat(b))
  return uniq(ids)
}

function sortToString(strOrObject) {
  let orderByWithDirection = strOrObject || ''
  if (orderByWithDirection && typeof strOrObject === 'object') {
    const { field, direction } = strOrObject
    // eslint-disable-next-line no-undef
    orderByWithDirection = [field, direction].join('_')
  }
  return orderByWithDirection
}

export const byNewest = (strOrObject = '') => {
  const orderByWithDirection = sortToString(strOrObject)
  return [
    NEWEST,
    'LAST_USER_MESSAGE_AT_DESC',
    'LAST_USER_OR_AGENT_MESSAGE_AT_DESC',
    'LAST_USER_OR_NOTE_MESSAGE_AT_DESC',
    'LAST_USER_AGENT_OR_NOTE_MESSAGE_AT_DESC',
    'LAST_UNANSWERED_USER_MESSAGE_AT_ASC',
  ].includes(orderByWithDirection)
}

export const byOldest = (strOrObject = '') => {
  const orderByWithDirection = sortToString(strOrObject)
  return [
    OLDEST,
    'LAST_USER_MESSAGE_AT_ASC',
    'LAST_USER_OR_AGENT_MESSAGE_AT_ASC',
    'LAST_USER_OR_NOTE_MESSAGE_AT_ASC',
    'LAST_USER_AGENT_OR_NOTE_MESSAGE_AT_ASC',
  ].includes(orderByWithDirection)
}

export const byLongestUnanswered = (strOrObject = '') => {
  const orderByWithDirection = sortToString(strOrObject)
  return LONGEST_UNANSWERED === orderByWithDirection
}

const sortUpdatedAtToCollaboratorUpdatedAtMapping = new Map([
  [NEWEST, NEWEST_BY_COLLABORATOR],
  [OLDEST, OLDEST_BY_COLLABORATOR],
])

const sortFuncs = {
  [NEWEST]: (a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at),
  [OLDEST]: (a, b) => Date.parse(a.updated_at) - Date.parse(b.updated_at),
  [NEWEST_BY_COLLABORATOR]: (a, b) =>
    Date.parse(b.latest_collaborator_comment_at) -
    Date.parse(a.latest_collaborator_comment_at),
  [OLDEST_BY_COLLABORATOR]: (a, b) =>
    Date.parse(a.latest_collaborator_comment_at) -
    Date.parse(b.latest_collaborator_comment_at),
  [SHORTEST_UNANSWERED]: (a, b) =>
    Date.parse(b.last_unanswered_user_message_at) -
    Date.parse(a.last_unanswered_user_message_at),
  [LONGEST_UNANSWERED]: (a, b) =>
    Date.parse(a.last_unanswered_user_message_at) -
    Date.parse(b.last_unanswered_user_message_at),
}

sortFuncs.default = sortFuncs[DEFAULT_SORT_ORDER]

export const sortSearchIds = (
  query,
  sortOrder,
  byId,
  sortByCollaboratorCommentAtEnabled
) => {
  const ids = extractSearchTicketIds(query)

  let sortFunc
  if (
    sortByCollaboratorCommentAtEnabled &&
    sortUpdatedAtToCollaboratorUpdatedAtMapping.has(sortOrder)
  ) {
    sortFunc =
      sortFuncs[sortUpdatedAtToCollaboratorUpdatedAtMapping.get(sortOrder)]
  } else {
    sortFunc = sortFuncs[sortOrder] || sortFuncs.default
  }

  const { queryId } = query
  const isIdQuery = !isNaN(queryId)
  const orderedResults = ids.sort((idA, idB) => {
    const a = byId[idA]
    const b = byId[idB]
    if (!a || !b) return 0
    if (isIdQuery) {
      if (a.id == queryId) return -1 // eslint-disable-line eqeqeq
      if (b.id == queryId) return 1 // eslint-disable-line eqeqeq
    }
    if (a.updated_at === b.updated_at) {
      if (a.id < b.id) return 1
      if (a.id > b.id) return -1
    }
    return sortFunc(a, b)
  })
  return orderedResults
}

export const sortByFunc = sort => sortFuncs[sort] || sortFuncs.default

const API_MAP = {
  [NEWEST]: 'updated_at desc',
  [OLDEST]: 'updated_at asc',
  [NEWEST_BY_COLLABORATOR]: 'latest_collaborator_comment_at desc',
  [OLDEST_BY_COLLABORATOR]: 'latest_collaborator_comment_at asc',
  [LONGEST_UNANSWERED]: 'last_unanswered_user_message_at asc',
  [SHORTEST_UNANSWERED]: 'last_unanswered_user_message_at desc',
}

API_MAP.default = API_MAP[DEFAULT_SORT_ORDER]

export const toGraphQL = (sort, sortByCollaboratorCommentAtEnabled) => {
  if (
    sortByCollaboratorCommentAtEnabled &&
    sortUpdatedAtToCollaboratorUpdatedAtMapping.has(sort)
  ) {
    return API_MAP[sortUpdatedAtToCollaboratorUpdatedAtMapping.get(sort)]
  }

  return API_MAP[sort] || API_MAP.default
}

export const toParam = sortOrder => {
  if (!sortOrder) return ''
  return `?sort=${encodeURIComponent(sortOrder)}`
}
